/**
 * Where each breadcrumb and Dashboard tab goes, as a real address (so a crumb
 * opens in a new tab) and as in-app navigation for a plain click. From a
 * settings page the move first leaves settings — asking about unsaved edits —
 * and waits for the page to step off its history entry.
 */
import type { NavTrailTarget } from '@turn-connect/shared';
import {
  accountDashboardHref,
  allAccountsHref,
  locationDashboardHref,
  navigateToAccountDashboard,
  navigateToAllAccounts,
  navigateToLocationDashboard,
  readAccountsPlace,
} from '../../../utils/navigation';
import { isSettingsDialogOpen, leaveSettingsThen, leaveTopSettingsThen, openSettings } from '../../../utils/settingsDialogs';
import { stripAccountSettingsLink, withAccountSettingsLink } from '../../../utils/accountSettingsLink';
import { stripLocationSettingsLink } from '../../../utils/locationSettingsLink';
import { whenSettingsHistoryIdle } from '../../../hooks/useSettingsDialogUrl';

/** The address without an open settings page's own target (a link opened from settings lands on the page, not in settings). */
const withoutSettings = (search: string) => stripLocationSettingsLink(stripAccountSettingsLink(search));

export function trailHref(target: NavTrailTarget, search: string = window.location.search): string | undefined {
  const from = withoutSettings(search);
  switch (target.kind) {
    case 'accounts':
      return allAccountsHref(from);
    case 'account':
      return accountDashboardHref(target.accountId, undefined, undefined, from);
    case 'location':
      return locationDashboardHref(target.accountId, target.locationId, undefined, from);
    case 'settings':
      // The account's settings (the way back from a location's): a real address too.
      return target.scope === 'account'
        ? `${window.location.pathname}${withAccountSettingsLink(from, { accountId: target.accountId, tab: target.section ?? 'details' })}`
        : undefined;
    default:
      return undefined;
  }
}

export function goToTrailTarget(target: NavTrailTarget): void {
  switch (target.kind) {
    case 'accounts':
      navigateToAllAccounts();
      return;
    case 'account':
      navigateToAccountDashboard(target.accountId);
      return;
    case 'location':
      navigateToLocationDashboard(target.accountId, target.locationId);
      return;
    case 'settings':
      openSettings(
        target.scope === 'account'
          ? { kind: 'account', accountId: target.accountId, ...(target.section && { tab: target.section }) }
          : { kind: 'location', locationId: target.locationId!, accountId: target.accountId }
      );
  }
}

/** Whether the page under the settings is already the target (then leaving settings is enough). */
export function isShowingTarget(
  target: NavTrailTarget,
  search: string = window.location.search,
  opts: { isSystemAdmin?: boolean } = {}
): boolean {
  const params = new URLSearchParams(search);
  const view = params.get('view');
  if (target.kind === 'accounts') return view === 'dashboard:accounts' && readAccountsPlace(search).scope === 'all';
  if (target.kind === 'account') {
    const place = readAccountsPlace(search);
    if (view !== 'dashboard:accounts' || place.groupId) return false;
    if (place.scope === 'account') return place.accountId === target.accountId;
    // No scope: everyone but a system admin (whose default is every account)
    // sees their own account — the one the address names.
    return place.scope === undefined && !opts.isSystemAdmin && place.accountId === target.accountId;
  }
  if (target.kind === 'location') return view === 'dashboard:location' && params.get('co') === target.locationId;
  return false;
}

/**
 * Leave every open settings page (asking about unsaved edits; "Keep editing"
 * cancels), then go to the target unless the page underneath already is it.
 */
export function leaveSettingsTo(target: NavTrailTarget, opts: { isSystemAdmin?: boolean } = {}): void {
  // Back to the account's settings from a location's opened on top of them:
  // leave only the location's page (it asks about its own unsaved edits), and
  // the account's — on the section it was on, with its edits — is in front again.
  if (target.kind === 'settings' && target.scope === 'account' && isSettingsDialogOpen('account', target.accountId)) {
    leaveTopSettingsThen(() => undefined);
    return;
  }
  leaveSettingsThen(() =>
    whenSettingsHistoryIdle(() => {
      if (!isShowingTarget(target, window.location.search, opts)) goToTrailTarget(target);
    })
  );
}
